import React, { useRef, useEffect } from "react";
import { Chart } from "chart.js/auto";
import { heatmapOptions } from "../config/chartOptions";
import { HEATMAP_COLORS } from "../config/chartColors";
import "chartjs-adapter-date-fns";

// Определяем цвет ячейки в зависимости от типа нарушения
const getPointColor = (data) => {
    if (!data) return "transparent";
    if (data.missed) return HEATMAP_COLORS.missed;
    if (data.delay > 0) return HEATMAP_COLORS.delay;
    if (data.overtime > 0) return HEATMAP_COLORS.overtime;
    if (data.earlyExit > 0) return HEATMAP_COLORS.earlyExit;

    return HEATMAP_COLORS.present; // Нет нарушений
};

// Определяем размер точки в зависимости от "серьезности" нарушения
const getPointRadius = (data) => {
    if (!data) return 0;

    if (data.missed) return 8; // Самый большой размер для пропусков

    const total =
        (data.delay || 0) + (data.overtime || 0) + (data.earlyExit || 0);

    if (total > 120) return 7; // Больше 2 часов нарушений
    if (total > 60) return 6; // Больше 1 часа нарушений
    if (total > 30) return 5; // Больше 30 минут
    if (total > 0) return 4; // Есть нарушения

    return 3; // Нет нарушений
};

// Рассчитываем min/max на основе data для оси X
const getXScaleLimits = (data) => {
    if (!data || data.length === 0) return { min: new Date(), max: new Date() };

    const dates = data.map((d) => new Date(d.x));
    const minDate = new Date(Math.min(...dates));
    const maxDate = new Date(Math.max(...dates));

    // Добавляем отступы по краям для лучшего отображения
    minDate.setDate(minDate.getDate() - 1); // сдвиг назад на 1 день
    maxDate.setDate(maxDate.getDate() + 1); // сдвиг вперёд на 1 день

    return { min: minDate, max: maxDate };
};

// Рассчитываем общее количество нарушений для каждой метки по оси Y
const calculateTotalViolations = (data, labels) => {
    if (!data || !labels || labels.length === 0) return {};

    const totals = {};

    // Инициализируем счетчики для каждой метки
    labels.forEach((label) => {
        totals[label] = {
            delayCount: 0,
            overtimeCount: 0,
            earlyExitCount: 0,
            missedCount: 0,
            totalCount: 0,
        };
    });

    // Подсчитываем нарушения для каждой метки
    data.forEach((point) => {
        const label = point.y;
        if (label && totals[label]) {
            if (point.delay > 0) totals[label].delayCount++;
            if (point.overtime > 0) totals[label].overtimeCount++;
            if (point.earlyExit > 0) totals[label].earlyExitCount++;
            if (point.missed) totals[label].missedCount++;

            totals[label].totalCount =
                totals[label].delayCount +
                totals[label].missedCount +
                totals[label].earlyExitCount;
        }
    });

    return totals;
};

// Сортируем и ограничиваем количество меток по заданной метрике и режиму отображения
const sortAndLimitLabels = (labels, metrics, { sortBy, viewMode, limit }) => {
    if (viewMode === "all")
        return [...labels].sort((a, b) => a.localeCompare(b, "ru"));
    return [...labels]
        .sort((a, b) => {
            const aVal = metrics[a]?.[sortBy] || 0;
            const bVal = metrics[b]?.[sortBy] || 0;
            return viewMode === "top" ? bVal - aVal : aVal - bVal;
        })
        .slice(0, limit);
};

// Отбираем точки, соответствующие отфильтрованным меткам
const filterPointsByLabels = (points, filteredLabels) => {
    //   if (!points || !filteredLabels) return points;
    const labelSet = new Set(filteredLabels);
    return points.filter((point) => labelSet.has(point.y));
};

// Форматирование минут в часы и минуты
const formatMinutes = (minutes) => {
    const hrs = Math.floor(minutes / 60);
    const mins = Math.round(minutes % 60);
    return `${hrs}ч ${mins.toString().padStart(2, "0")}м`;
};

// Тепловая карта посещаемости
export const AttendanceHeatmap = ({
    data,
    sortBy = "totalCount",
    viewMode = "top",
    limit = 10,
    height = "300px",
}) => {
    const chartRef = useRef(null);
    const chartInstance = useRef(null);
    const groupBy = data.groupBy;

    useEffect(() => {
        if (!data || !chartRef.current) return;

        const ctx = chartRef.current.getContext("2d");
        if (!ctx) return;

        if (chartInstance.current) {
            chartInstance.current.destroy();
        }
        const { points, labels: originalLabels } = data;

        if (!points || points.length === 0) return;

        const metrics = calculateTotalViolations(points, originalLabels);
        const filteredLabels = sortAndLimitLabels(originalLabels, metrics, {
            sortBy,
            viewMode,
            limit,
        });
        const filteredPoints = filterPointsByLabels(points, filteredLabels);
        const { min, max } = getXScaleLimits(filteredPoints);

        const dataset = {
            label: "Посещаемость",
            data: filteredPoints.map((point) => ({
                x: new Date(point.x),
                y: point.y,
                r: getPointRadius(point),
                delay: point.delay,
                overtime: point.overtime,
                earlyExit: point.earlyExit,
                missed: point.missed,
            })),
            backgroundColor: filteredPoints.map(getPointColor),
            hoverBackgroundColor: filteredPoints.map(getPointColor),
            borderColor: "#3E3E42",
            borderWidth: 1,
        };

        const localHeatmapOptions = {
            ...heatmapOptions,
            scales: {
                ...heatmapOptions.scales,
                x: {
                    ...heatmapOptions.scales.x,
                    min,
                    max,
                    grid: {
                        color: "#3E3E42",
                    },
                    ticks: {
                        color: "#E5E7EB",
                    },
                },
                y: {
                    ...heatmapOptions.scales.y,
                    labels: filteredLabels,
                    title: {
                        display: true,
                        text:
                            {
                                department: "Отделы",
                                shift: "Смены",
                                employee: "Сотрудники",
                                company: "Компания",
                            }[groupBy] || "Группа",
                        color: "#E5E7EB",
                    },
                    grid: {
                        color: "#3E3E42",
                    },
                    ticks: {
                        color: "#E5E7EB",
                    },
                },
            },
            plugins: {
                ...heatmapOptions.plugins,
                tooltip: {
                    ...heatmapOptions.plugins.tooltip,
                    backgroundColor: "#1E1E1E",
                    titleColor: "#E5E7EB",
                    bodyColor: "#E5E7EB",
                    borderColor: "#3E3E42",
                    callbacks: {
                        label: (context) => {
                            const { raw } = context;
                            if (!raw) return "Нет данных";

                            const delay = raw.delay || 0;
                            const overtime = raw.overtime || 0;
                            const earlyExit = raw.earlyExit || 0;

                            if (raw.missed) {
                                return ["Пропуск"];
                            }

                            const level =
                                context.chart?.options?.scales?.y?.title?.text?.toLowerCase();

                            const lines = [];

                            if (level === "сотрудники") {
                                if (delay)
                                    lines.push(
                                        `• Опоздание: ${formatMinutes(delay)}`
                                    );
                                if (overtime)
                                    lines.push(
                                        `• Переработка: ${formatMinutes(
                                            overtime
                                        )}`
                                    );
                                if (earlyExit)
                                    lines.push(
                                        `• Ранний уход: ${formatMinutes(
                                            earlyExit
                                        )}`
                                    );
                            } else {
                                if (delay)
                                    lines.push(
                                        `• Опозданий: ${formatMinutes(delay)}`
                                    );
                                if (overtime)
                                    lines.push(
                                        `• Переработок: ${formatMinutes(
                                            overtime
                                        )}`
                                    );
                                if (earlyExit)
                                    lines.push(
                                        `• Ранних уходов: ${formatMinutes(
                                            earlyExit
                                        )}`
                                    );
                            }

                            return lines.length ? lines : ["Без нарушений"];
                        },
                    },
                },
            },
        };

        chartInstance.current = new Chart(ctx, {
            type: "bubble",
            data: {
                datasets: [dataset],
            },
            options: localHeatmapOptions,
        });

        return () => {
            if (chartInstance.current) {
                chartInstance.current.destroy();
            }
        };
    }, [data, sortBy, viewMode, limit, groupBy]);

    // Легенда для тепловой карты
    const renderLegend = () => (
        <div className="flex flex-wrap gap-4 mt-4 justify-center text-xs">
            <div className="flex items-center">
                <div
                    className="w-3 h-3 rounded-full mr-1"
                    style={{ backgroundColor: HEATMAP_COLORS.present }}
                ></div>
                <span>Присутствие</span>
            </div>
            <div className="flex items-center">
                <div
                    className="w-3 h-3 rounded-full mr-1"
                    style={{ backgroundColor: HEATMAP_COLORS.delay }}
                ></div>
                <span>Опоздание</span>
            </div>
            <div className="flex items-center">
                <div
                    className="w-3 h-3 rounded-full mr-1"
                    style={{ backgroundColor: HEATMAP_COLORS.overtime }}
                ></div>
                <span>Переработка</span>
            </div>
            <div className="flex items-center">
                <div
                    className="w-3 h-3 rounded-full mr-1"
                    style={{ backgroundColor: HEATMAP_COLORS.earlyExit }}
                ></div>
                <span>Ранний уход</span>
            </div>
            <div className="flex items-center">
                <div
                    className="w-3 h-3 rounded-full mr-1"
                    style={{ backgroundColor: HEATMAP_COLORS.missed }}
                ></div>
                <span>Пропуск</span>
            </div>
        </div>
    );

    if (!data || !data.points || data.points.length === 0) {
        return (
            <div
                className="flex justify-center items-center bg-[#2D2D30] rounded border border-[#3E3E42]"
                style={{ height: height }}
                // role="alert"
                aria-label="Нет данных для отображения"
            >
                <p className="text-gray-400">Нет данных для отображения</p>
            </div>
        );
    }

    return (
        <div
            style={{ height: height }}
            role="img"
            aria-label="Тепловая карта посещаемости"
        >
            <canvas ref={chartRef} />
        </div>
    );
};
